/**
 * Roles and permissions. The server is the only authority — the web app uses these
 * helpers purely to decide what to render. Every API route re-checks. See docs/SECURITY.md.
 */
export const ROLES = [
    'SUPER_ADMIN',
    'OWNER',
    'OPERATIONS_MANAGER',
    'DISPATCHER',
    'CUSTOMER_SERVICE',
    'ACCOUNTING',
    'DRIVER_MANAGER',
    'DRIVER',
    'BUSINESS_ADMIN',
    'BUSINESS_MEMBER',
    'CUSTOMER',
];
export const PERMISSIONS = [
    // Deliveries
    'delivery:create',
    'delivery:read:own',
    'delivery:read:org',
    'delivery:read:all',
    'delivery:update',
    'delivery:cancel',
    'delivery:modify_price',
    // Dispatch
    'dispatch:view',
    'dispatch:assign',
    'dispatch:reassign',
    'dispatch:override_sla',
    // Driver
    'driver:work',
    'driver:read:all',
    'driver:manage',
    'driver:earnings:read:own',
    'driver:earnings:read:all',
    // Money
    'payment:charge',
    'payment:refund',
    'invoice:read',
    'invoice:manage',
    'credit:issue',
    // Intelligence
    'report:operations',
    'report:financial',
    'margin:view',
    // Administration
    'customer:manage',
    'settings:manage',
    'user:manage',
    'audit:read',
];
const OPERATIONS_BASE = [
    'delivery:create',
    'delivery:read:all',
    'delivery:update',
    'delivery:cancel',
    'dispatch:view',
    'dispatch:assign',
    'dispatch:reassign',
    'driver:read:all',
    'report:operations',
];
export const ROLE_PERMISSIONS = {
    SUPER_ADMIN: PERMISSIONS,
    OWNER: PERMISSIONS,
    OPERATIONS_MANAGER: [
        ...OPERATIONS_BASE,
        'delivery:modify_price',
        'dispatch:override_sla',
        'driver:manage',
        'driver:earnings:read:all',
        'margin:view',
        'report:financial',
        'customer:manage',
        'credit:issue',
    ],
    DISPATCHER: [...OPERATIONS_BASE, 'margin:view'],
    CUSTOMER_SERVICE: [
        'delivery:create',
        'delivery:read:all',
        'delivery:update',
        'dispatch:view',
        'customer:manage',
        'invoice:read',
    ],
    ACCOUNTING: [
        'delivery:read:all',
        'invoice:read',
        'invoice:manage',
        'payment:charge',
        'payment:refund',
        'credit:issue',
        'driver:earnings:read:all',
        'margin:view',
        'report:financial',
    ],
    DRIVER_MANAGER: [
        'delivery:read:all',
        'dispatch:view',
        'driver:read:all',
        'driver:manage',
        'driver:earnings:read:all',
        'report:operations',
    ],
    DRIVER: ['driver:work', 'delivery:read:own', 'driver:earnings:read:own'],
    BUSINESS_ADMIN: [
        'delivery:create',
        'delivery:read:org',
        'delivery:cancel',
        'invoice:read',
        'report:operations',
        'user:manage',
    ],
    BUSINESS_MEMBER: ['delivery:create', 'delivery:read:own'],
    CUSTOMER: ['delivery:create', 'delivery:read:own'],
};
export function permissionsFor(roles) {
    const list = Array.isArray(roles) ? roles : [roles];
    const set = new Set();
    for (const role of list)
        for (const p of ROLE_PERMISSIONS[role] ?? [])
            set.add(p);
    return [...set];
}
export function hasPermission(roles, permission) {
    const list = Array.isArray(roles) ? roles : [roles];
    return list.some((role) => (ROLE_PERMISSIONS[role] ?? []).includes(permission));
}
export function hasAnyPermission(roles, permissions) {
    return permissions.some((p) => hasPermission(roles, p));
}
/** Roles that reach the internal Command Center surfaces. */
export const STAFF_ROLES = [
    'SUPER_ADMIN',
    'OWNER',
    'OPERATIONS_MANAGER',
    'DISPATCHER',
    'CUSTOMER_SERVICE',
    'ACCOUNTING',
    'DRIVER_MANAGER',
];
export function isStaff(roles) {
    const list = Array.isArray(roles) ? roles : [roles];
    return list.some((role) => STAFF_ROLES.includes(role));
}
/** Where a user lands after signing in. */
export function homeRouteFor(roles) {
    if (roles.includes('DRIVER'))
        return '/driver';
    if (roles.some((r) => r === 'OWNER' || r === 'SUPER_ADMIN'))
        return '/command/executive';
    if (isStaff(roles))
        return '/command/dispatch';
    if (roles.includes('BUSINESS_ADMIN') || roles.includes('BUSINESS_MEMBER'))
        return '/portal';
    return '/portal';
}
//# sourceMappingURL=roles.js.map